<template>
  <DashboardWindow>
    <template v-slot:title class="mb-4">
      <span class="d-none d-lg-inline">
        {{ $t('shoppingLists') }}
      </span>
    </template>

    <template v-slot:title-right>
      <form
        class="new-list-wrap d-flex justify-content-between align-items-center"
        @submit.prevent="addList"
        @keydown.enter.prevent="addList"
      >
        <div class="add-list--input" :class="{ 'focusable-area': hasFocus }">
          <WCInputGroup>
            <WCInput
              class="form-control"
              type="text"
              :placeholder="$t('typeListName')"
              v-model="form.name"
              :aria-label="$t('addNewList')"
              @focusin="hasFocus = true"
              @focusout="hasFocus = false"
            />
            <template v-slot:right>
              <button
                class="btn btn-primary wc-add-list__btn"
                :disabled="$v.form.name.$invalid"
                type="submit"
                :aria-label="$t('addNewList')"
              >
                <font-awesome-icon icon="plus" /><span class="ml-1">{{ $t('addNewList') }}</span>
              </button>
            </template>
          </WCInputGroup>
          <div class="text-danger" v-if="form.name && $v.form.name.$invalid">
            {{ $t('listNamesCannotExceed') }}
          </div>
        </div>
      </form>
    </template>

    <template v-slot:body>
      <DashboardWindowSection>
        <WCSpinner v-if="isLoading" size="xl" position="center" />
        <div class="mt-n4 mx-n4" v-else>
          <!-- START : List of Shopping List Entries -->
          <WCShoppingList :shoppingList="favoritesList" icon="heart"> </WCShoppingList>
          <WCShoppingList :shoppingList="saveForLaterList" icon="bookmark"></WCShoppingList>
          <WCShoppingList
            v-for="list in customLists"
            :key="list.id"
            :shoppingList="list"
            :hasDelete="true"
            @removeList="removeList(list)"
          />
          <!-- END : List of Shopping List Entries -->
        </div>
      </DashboardWindowSection>
    </template>
  </DashboardWindow>
</template>

<script>
import WCInput from '@/modules/forms/components/WCInput/WCInput.vue';
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import WCInputGroup from '@/modules/forms/components/WCInputGroup/WCInputGroup.vue';
import WCSimpleToast from '@/modules/toasts/components/WCSimpleToast/WCSimpleToast.vue';
import ToastService from '@/modules/toasts/services/ToastService';
import { mapActions, mapGetters } from 'vuex';
import DashboardWindow from '@/layouts/components/Dashboard/DashboardWindow.vue';
import DashboardWindowSection from '@/layouts/components/Dashboard/DashboardWindowSection.vue';
import { required, maxLength } from 'vuelidate/lib/validators';
import WCShoppingList from '../components/WCShoppingList/WCShoppingList.vue';

export default {
  name: 'Lists',
  components: {
    DashboardWindowSection,
    DashboardWindow,
    WCInput,
    WCInputGroup,
    WCShoppingList,
    WCSpinner,
  },
  data() {
    return {
      form: {
        name: null,
      },
      hasFocus: false,
    };
  },
  validations() {
    return {
      form: {
        name: { required, maxLength: maxLength(20) },
      },
    };
  },
  async mounted() {
    await this.fetch();
  },
  computed: {
    ...mapGetters({
      favoritesList: 'lists/getFavoritesList',
      customLists: 'lists/getCustomLists',
      saveForLaterList: 'lists/getSaveForLaterList',
      isLoading: 'lists/isLoading',
    }),
  },
  methods: {
    ...mapActions({
      add: 'lists/add',
      remove: 'lists/remove',
      fetch: 'lists/fetch',
    }),
    async addList() {
      if (this.form.name && this.form.name.length <= 20) {
        try {
          await this.add(this.form);
          this.form = {};
        } catch (error) {
          ToastService.open(WCSimpleToast, {
            props: {
              variant: 'danger',
              title: 'Error',
              message: error.message,
            },
            timeout: 7000,
          });
        }
      }
    },
    async removeList(shoppingList) {
      try {
        const id = shoppingList.id;
        await this.remove(id);
        // Notify Success
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'success',
            title: `Deleted ${shoppingList.name}`,
            message: `The ${shoppingList.name} shopping List has been removed.`,
          },
          timeout: 7000,
        });
      } catch (error) {
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'danger',
            title: 'Error',
            message: error.message,
          },
          timeout: 7000,
        });
      }
    },
  },
};
</script>
<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.table-header {
  table-layout: fixed;
}

.wc-header {
  background-color: var(--secondary-lighter-9, $light);
}

.new-list-wrap {
  width: rem(280px);
  @include media-breakpoint-down(xs) {
    width: 100%;
  }
}

.add-list--input {
  .form-control:focus {
    box-shadow: none;
  }
}
</style>
